import get from 'lodash/get'
import { dayjs } from '@/composables/useDayjs'
import widgetDataTypes, { CARD_DATA_TYPES } from '@/enum/widgetDataTypes'
import { DEFAULT_TIME_DELAY, queueActivityGaugeKey } from '@/enum/generic'
import { realTimeTableKey } from '@/enum/realtimeTableConfig'
import { getRequestsInfoByWidgetId } from '@/composables/useAxiosRequestDuration'
import { getWidgetTemplate } from '@/composables/globalData/useTemplatesCategoryData'
import { getActiveLayoutDelayInterval, getActiveLayoutMinRefreshInterval } from '@/composables/globalData/useLayouts'

// minimum refresh interval for real-time widgets - 10 seconds
const MIN_REFRESH_INTERVAL = 10
const MULTI_QUEUES_DASHBOARD_KEY = 'GetMultiQueuesDashboard'
const MULTI_QUEUE_RULES_DASHBOARD_KEY = 'GetMultiQueuesDashboardRule'
const AI_QUESTIONS_PIE_CHART_KEY = 'AIQuestionsPieChart'
let refreshIntervalLocal = 0

const CHART_DATA_TYPE_IDS = [
    widgetDataTypes.LINES_TYPE_ID,
    widgetDataTypes.BARS_WITH_LINES_TYPE_ID,
    widgetDataTypes.X_AXIS_TYPE_ID,
    widgetDataTypes.CHART_QUEUE,
    widgetDataTypes.PIE_TYPE_ID,
    widgetDataTypes.CHART_AREA_ID,
    widgetDataTypes.POLAR_CHART_TYPE_ID,
    widgetDataTypes.HEATMAP_CHART_TYPE_ID,
]

const WIDGETS_WITHOUT_TIME_FRAME = [
    widgetDataTypes.COUNTER_TYPE_ID,
    widgetDataTypes.QUEUE_COUNTER_TYPE_ID,
    widgetDataTypes.AI_SUMMARY_TEXT,
    widgetDataTypes.AI_SUMMARY_BULLETS_LIST,
    widgetDataTypes.AI_SUMMARY_NUMBERS_LIST,
    widgetDataTypes.INFO_TYPE_ID,
    widgetDataTypes.DIALERS_TYPE_ID,
    widgetDataTypes.CLOCK_AND_DATE_TYPE_ID,
    widgetDataTypes.VOICEBOT_COUNTER_TYPE_ID
]

const WIDGETS_WITHOUT_REFRESH_INTERVAL = [
    ...WIDGETS_WITHOUT_TIME_FRAME,

]

const AI_SUMMARY_WIDGET_DATA_TYPES = [
    widgetDataTypes.AI_SUMMARY_TEXT,
    widgetDataTypes.AI_SUMMARY_BULLETS_LIST,
    widgetDataTypes.AI_SUMMARY_NUMBERS_LIST
]

const minRefreshInterval = () => {
    return getActiveLayoutMinRefreshInterval() || MIN_REFRESH_INTERVAL
}

export function getWidgetRefreshInterval (widget) {
    let timeToLoadData = 0
    const widgetTemplate = getWidgetTemplate(widget.TemplateID)
    let widgetRefreshInterval = get(widgetTemplate, 'DefaultRefreshInterval', '')

    const minInterval = minRefreshInterval()
    widgetRefreshInterval = Number(widgetRefreshInterval) <= minInterval ? minInterval : Number(widgetRefreshInterval)

    const widgetStoreData = getRequestsInfoByWidgetId(widget.WidgetID)
    if (widgetStoreData) {
        timeToLoadData = widgetStoreData.duration
    }

    refreshIntervalLocal = timeToLoadData && refreshIntervalLocal < timeToLoadData && widgetRefreshInterval < timeToLoadData ? timeToLoadData : widgetRefreshInterval

    return refreshIntervalLocal * 1000
}

export function getWidgetEndpoint (widget) {
    const widgetTemplate = getWidgetTemplate(widget.TemplateID)
    const endpoint = get(widgetTemplate, 'Endpoint', '')
    return endpoint.replace('{WidgetID}', widget.WidgetID)
}

export function getWidgetDataType (widget) {
    let dataType = widget.DataTypeID
    if (!dataType) {
        const widgetTemplate = getWidgetTemplate(widget.TemplateID)
        dataType = get(widgetTemplate, 'DataType.DataTypeID')
    }
    return dataType
}

// export const componentWidth = {
//     [widgetDataTypes.COUNTER_TYPE_ID]: 'lg:w-auto',
//     [widgetDataTypes.QUEUE_COUNTER_TYPE_ID]: 'lg:w-auto',
//     [widgetDataTypes.TOTAL_OUTGOING_CALLS]: 'lg:w-auto',
//     [widgetDataTypes.PIE_TYPE_ID]: '',
//     default: 'lg:w-3/3',
// }

// export function getDataTypeClass (widget) {
//     let dataType = 'default'
//     try {
//         dataType = getWidgetDataType(widget)
//     } catch (e) {
//         console.warn(e)
//     }
//     return componentWidth[dataType]
// }

export function isRealtimeWidget (widget) {
    if (widget?.EndPoint?.includes(realTimeTableKey)) {
        return true
    } else if (isWidgetType(widget, widgetDataTypes.EXTENSION_CARDS)) {
        return true
    }
    return false
}

export function isMultiQueuesDashboard (widget) {
    return !isMultiQueueRulesDashboard(widget) && widget.EndPoint?.includes(MULTI_QUEUES_DASHBOARD_KEY)
}

export function isMultiQueueRulesDashboard (widget) {
    return widget.EndPoint?.includes(MULTI_QUEUE_RULES_DASHBOARD_KEY)
}

export function isPieWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.PIE_TYPE_ID) || widget.WidgetLayout?.ComponentTypeID === widgetDataTypes.PIE_TYPE_ID
}

export function isExternalDataWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.EXTERNAL_DATA_TYPE_ID)
}

export function isQueueTable (widget) {
    return isWidgetType(widget, widgetDataTypes.QUEUE_ACTIVE_CALL)
}

export function isTableWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.TABLE_TYPE_ID)
}

export function isTableWidgetByTypeId (dataTypeId) {
    return dataTypeId === widgetDataTypes.TABLE_TYPE_ID
}

export function isQueueGauge (widget) {
    return isWidgetType(widget, widgetDataTypes.CHART_SPEEDOMETER)
}

export function isWidgetCard (widget) {
    return CARD_DATA_TYPES.includes(getWidgetTypeId(widget))
}

export function isQueueChart (widget) {
    return isWidgetType(widget, widgetDataTypes.CHART_QUEUE)
}

export function isQueueDashboardWidget (widget) {
    const typeId = getWidgetTypeId(widget)

    return typeId === widgetDataTypes.QUEUE_DASHBOARD
        && !isQueueActivityGauge(widget)
}

export function isQueueActivityGauge (widget) {
    return widget.EndPoint?.includes(queueActivityGaugeKey)
}

export function isAiQuestionsPieChartWidget (widget) {
    return widget?.EndPoint?.includes(AI_QUESTIONS_PIE_CHART_KEY)
}

export function isAreaChartWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.CHART_AREA_ID)
}

export function isHtmlEditor (widget) {
    return isWidgetType(widget, widgetDataTypes.RICH_TEXT_EDITOR_ID)
}

export function isExtensionCardStatisticsWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.EXTENSION_CARDS_STATISTICS)
}

export function isExtensionCardStatisticsWidgetByTypeId (dataTypeId) {
    return dataTypeId === widgetDataTypes.EXTENSION_CARDS_STATISTICS
}

export function isNoteListWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.RICH_TEXT_EDITOR_LIST)
}


export function isARealtimeTableWidget (widget) {
    const isQueueWidgetType = [ widgetDataTypes.QUEUE_DASHBOARD, widgetDataTypes.QUEUE_ACTIVE_CALL ]?.includes(getWidgetTypeId(widget))
    return isQueueWidgetType || isRealtimeWidget(widget) || isMultiQueuesDashboard(widget)
}

/**
 * Returns date range for relative time filter (Date_interval, datedeff)
 * @param {Object} widget - Widget with WidgetTime configuration
 * @returns {{ start: Date, end: Date }}
 */
function getRelativeDates (widget) {
    const start = new Date()
    const end = new Date()

    const startDateOffset = get(widget, 'WidgetTime.Date_interval', 0)
    const dateInterval = get(widget, 'WidgetTime.datedeff', 0)

    start.setDate(start.getDate() - startDateOffset)
    end.setDate(end.getDate() - (startDateOffset - dateInterval))

    return {
        start,
        end
    }
}

/**
 * Returns date range for absolute time filter (Date_start, Date_end)
 * @param {Object} widget - Widget with WidgetTime configuration
 * @returns {{ start: Date, end: Date }}
 */
function getAbsoluteDates (widget) {
    const startDate = get(widget, 'WidgetTime.Date_start', new Date().toISOString())
    const endDate = get(widget, 'WidgetTime.Date_end', new Date().toISOString())

    return {
        start: dayjs(startDate).toDate(),
        end: dayjs(endDate).toDate()
    }
}

function removeYearFromDateFormat (toFormat) {
    return toFormat
        .replace(/[\.\/\-_\s]*YYYY$/i, '')
        .replace(/^YYYY[\.\/\-_\s]*/i, '')
}

function formatWidgetDateRange (start, end, toFormat) {
    const startDate = dayjs(start)
    const endDate = dayjs(end)

    if (startDate.year() === endDate.year()) {
        const startFormat = removeYearFromDateFormat(toFormat)
        return `${startDate.format(startFormat)} – ${endDate.format(toFormat)}`
    }

    return `${startDate.format(toFormat)} – ${endDate.format(toFormat)}`
}

export function timeFilterToHuman (widget, toFormat = 'DD/MM/YYYY') {
    const { start, end } = getRelativeDates(widget)
    return formatWidgetDateRange(start, end, toFormat)
}

export function timeFilterForAbsolute (widget, toFormat = 'DD/MM/YYYY') {
    const { start, end } = getAbsoluteDates(widget)
    return formatWidgetDateRange(start, end, toFormat)
}

export function timeFilterForPeriod (typeOfPeriod = 'quarter', periodsBack = 0, toFormat = 'DD/MM/YYYY', calendaric = false, weekStartDay = 0) {
    const { start, end } = getPeriodDates(typeOfPeriod, periodsBack, calendaric, weekStartDay)
    return formatWidgetDateRange(start, end, toFormat)
}

/**
 * Returns the date range display string for a widget based on its WidgetTime (period / absolute / relative).
 * Use in table header and export filename for consistent display.
 * @param {Object} widget - Widget with WidgetTime configuration
 * @param {string} [toFormat='DD.MM.YYYY'] - dayjs format for the output dates
 * @returns {string} - Formatted date range string (e.g. "08.03  -  14.03.2026" or "08.03.2026  -  14.03.2027")
 */
export function getWidgetDateInterval (widget, toFormat = 'DD.MM.YYYY') {
    const widgetTime = get(widget, 'WidgetTime', {})

    if (widgetTime.type === 'period') {
        return timeFilterForPeriod(
            widgetTime.period_type || 'quarter',
            widgetTime.periods_back ?? 0,
            toFormat,
            widgetTime.is_calendrical || false,
            widgetTime.is_monday_first ? 1 : 0
        )
    }

    if (widgetTime.type === 'absolute') {
        return timeFilterForAbsolute(widget, toFormat)
    }

    return timeFilterToHuman(widget, toFormat)
}

function getPeriodDates (periodType, periodsBack, calendaric, weekStartDay) {
    const today = new Date()
    let start, end

    const periodFunctions = {
        day: {
            startOfPeriod: (date) => dayjs(date).startOf('day').toDate(),
            subPeriods: (date, n) => dayjs(date).subtract(n, 'day').toDate(),
            endOfPeriod: (date) => dayjs(date).endOf('day').toDate(),
            adjustDate: (date, periodsBack) => date.setDate(date.getDate() - periodsBack),
        },
        week: {
            startOfPeriod: (date) => {
                const d = dayjs(date)
                if (weekStartDay === 1) {
                    return d.startOf('isoWeek').toDate()
                }
                return d.startOf('week').toDate()
            },
            subPeriods: (date, n) => dayjs(date).subtract(n, 'week').toDate(),
            endOfPeriod: (date) => {
                const d = dayjs(date)
                if (weekStartDay === 1) {
                    return d.endOf('isoWeek').toDate()
                }
                return d.endOf('week').toDate()
            },
            adjustDate: (date, periodsBack) => date.setDate(date.getDate() - periodsBack * 7),
        },
        month: {
            startOfPeriod: (date) => dayjs(date).startOf('month').toDate(),
            subPeriods: (date, n) => dayjs(date).subtract(n, 'month').toDate(),
            endOfPeriod: (date) => dayjs(date).endOf('month').toDate(),
            adjustDate: (date, periodsBack) => date.setMonth(date.getMonth() - periodsBack),
        },
        year: {
            startOfPeriod: (date) => dayjs(date).startOf('year').toDate(),
            subPeriods: (date, n) => dayjs(date).subtract(n, 'year').toDate(),
            endOfPeriod: (date) => dayjs(date).endOf('year').toDate(),
            adjustDate: (date, periodsBack) => date.setFullYear(date.getFullYear() - periodsBack),
        },
        quarter: {
            startOfPeriod: (date) => dayjs(date).startOf('quarter').toDate(),
            subPeriods: (date, n) => dayjs(date).subtract(n, 'quarter').toDate(),
            endOfPeriod: (date) => dayjs(date).endOf('quarter').toDate(),
            adjustDate: (date, periodsBack) => date.setMonth(date.getMonth() - periodsBack * 3),
        },
    }

    const periodFuncs = periodFunctions[periodType] || periodFunctions.quarter

    if (!calendaric) {
        start = new Date(today)
        end = today
        periodFuncs.adjustDate(start, periodsBack)
    } else {
        const currentPeriodStart = periodFuncs.startOfPeriod(today)
        start = periodFuncs.subPeriods(currentPeriodStart, periodsBack)

        if (Number(periodsBack) === 0) {
            end = periodFuncs.endOfPeriod(start)
        } else {
            const previousPeriodStart = periodFuncs.subPeriods(currentPeriodStart, 1)
            end = periodFuncs.endOfPeriod(previousPeriodStart)
        }
    }

    return {
        start,
        end
    }
}

/**
 * Returns the date range for a widget based on its WidgetTime configuration
 * Reuses getRelativeDates, getAbsoluteDates, and getPeriodDates functions
 * @param {Object} widget - The widget object containing WidgetTime
 * @returns {{ start: Date, end: Date }} - The start and end dates
 */
export function getWidgetDateRange (widget) {
    const widgetTime = get(widget, 'WidgetTime', {})

    if (widgetTime.type === 'period') {
        return getPeriodDates(
            widgetTime.period_type || 'quarter',
            widgetTime.periods_back || 0,
            widgetTime.is_calendrical || false
        )
    }

    if (widgetTime.type === 'absolute') {
        return getAbsoluteDates(widget)
    }

    // Default: relative dates
    return getRelativeDates(widget)
}

export function isChartWidget (widget) {
    return CHART_DATA_TYPE_IDS?.includes(getWidgetTypeId(widget)) || isQueueActivityGauge(widget) || isPieWidget(widget) || isQueueGauge(widget) || isFunnelChartWidget(widget)
}

export function getWidgetTypeId (widget) {
    return widget?.DataTypeID ?? widget?.DataType?.DataTypeID ?? null
}

export function isCounterAgentsInStatus (widget) {
    return getWidgetTypeId(widget) === widgetDataTypes.COUNTER_TYPE_ID
}

export function isWidgetType (widget, typeId) {
    return getWidgetTypeId(widget) === typeId
}

export function isQueueCounterWidgetType (widget) {
    return isWidgetType(widget, widgetDataTypes.QUEUE_COUNTER_TYPE_ID)
}

export function isTotalOutgoingCallType (widget) {
    return isWidgetType(widget, widgetDataTypes.TOTAL_OUTGOING_CALLS)
}

export function isAverageCallsDurationCard (widget) {
    return isWidgetType(widget, widgetDataTypes.AVERAGE_CALLS_DURATION)
}

export function isVoicebotCounterWidgetType (widget) {
    return isWidgetType(widget, widgetDataTypes.VOICEBOT_COUNTER_TYPE_ID)
}

export function isAiCounterWidgetType (widget) {
    return isWidgetType(widget, widgetDataTypes.AI_COUNTER_TYPE_ID)
}

export function isFunnelChartWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.INFO_TYPE_ID)
}

export function isSocketsRealTimeTableWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.DIALERS_TYPE_ID)
}


/**
 * Returns true if the widget type should show the time frame selector
 * @param {Object} widget - Widget object or DataType object
 * @returns {boolean}
 */
export function shouldShowTimeFrame (widget) {
    const dataTypeId = getWidgetTypeId(widget)
    if (!dataTypeId) {
        return true // Default to showing time frame if no type found
    }
    return !WIDGETS_WITHOUT_TIME_FRAME.includes(dataTypeId)
}

export function shouldShowRefreshInterval (widget) {
    const dataTypeId = getWidgetTypeId(widget)
    if (!dataTypeId) {
        return true // Default to showing time frame if no type found
    }
    return !WIDGETS_WITHOUT_REFRESH_INTERVAL.includes(dataTypeId)

}

/**
 * Returns true if at least one widget in the list should show the time frame
 * @param {Array} widgets - Array of widget objects
 * @returns {boolean}
 */
export function shouldShowTimeFrameForWidgets (widgets) {
    if (!Array.isArray(widgets) || widgets.length === 0) {
        return true
    }
    return widgets.some(widget => shouldShowTimeFrame(widget))
}

export function isClockAndDateWidget (widget) {
    return isWidgetType(widget, widgetDataTypes.CLOCK_AND_DATE_TYPE_ID)
}

export function getWidgetParameterConfig (widgetTemplate, parameterId, keysToInclude = []) {
    let result = {}

    if (widgetTemplate && widgetTemplate.DefaultWidgetConfig) {
        const currentWidgetParameterDefaultConfig = widgetTemplate.DefaultWidgetConfig.find((el) => el.ParameterID === parameterId)

        if (currentWidgetParameterDefaultConfig) {
            result = { ...currentWidgetParameterDefaultConfig }

            if (keysToInclude.length) {
                result = keysToInclude.reduce((acc, key) => {
                    if (currentWidgetParameterDefaultConfig[key]) {
                        acc[key] = currentWidgetParameterDefaultConfig[key]
                    }

                    return acc
                },
                {}
                )
            }
        }
    }

    return result
}

/**
 * Check if widget is an AI Summary widget
 * @param {Object} widget - Widget object
 * @returns {boolean} True if widget is an AI summary widget
 */
export function isAiSummaryWidget (widget) {
    const dataTypeId = getWidgetTypeId(widget)
    return AI_SUMMARY_WIDGET_DATA_TYPES.includes(dataTypeId)
}


export const getDefaultTimeDelay = () => {
    const minutes = getActiveLayoutDelayInterval() || DEFAULT_TIME_DELAY
    return minutes * 60 * 100
}
